import React, { useMemo, useState, useEffect } from 'react';
import { Button, Modal, Form, Input, message, Card, Popconfirm, InputNumber, Checkbox, Typography, Collapse, Select, Tag, Upload, Image, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, CloseOutlined, MinusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, SwapOutlined, CheckOutlined, HolderOutlined, UploadOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';
import { fetchPujas, createPuja, updatePuja, deletePuja, updatePujaLine, deletePujaLine, addPujaLines, addPujaBidhiLink, addPujaSankalpaLink, removePujaBidhiLink, removePujaSankalpaLink, updatePujaBidhiLinkOrder, bulkUpdatePujaLineOrder, addPujaToState } from '../store/pujaSlice';
import { Bidhi, CreatePuja, Puja, PujaLine, Sankalpa } from '../types';
import { fetchBidhis } from '../store/bidhiSlice';
import { fetchSankalpas } from '../store/sankalpaSlice';
import { pujaLineLinkApi, pujaTagAPI, pujaApi, API_URL } from '../services/api';
import AddPujaModal from '../components/AddPujaModal';
import AddLineModal from '../components/AddLineModal';
import PujaLinkingSelectionModal from '../components/puja/PujaLinkingSelectionModal';
import PujaBidhiLinkModal from '../components/puja/PujaBidhiLinkModal';
import PujaSankalpaLinkModal from '../components/puja/PujaSankalpaLinkModal';

const PujaPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { pujas, loading } = useSelector((state: RootState) => state.puja);
  const { bidhis } = useSelector((state: RootState) => state.bidhi);
  const { sankalpas } = useSelector((state: RootState) => state.sankalpa);
  
  const [addPujaModalVisible, setAddPujaModalVisible] = useState<boolean>(false);
  const [editPujaModalVisible, setEditPujaModalVisible] = useState<boolean>(false);
  const [editLineModalVisible, setEditLineModalVisible] = useState<boolean>(false);
  const [addLineModalVisible, setAddLineModalVisible] = useState<boolean>(false);
  const [linkingModalVisible, setLinkingModalVisible] = useState<boolean>(false);
  const [bidhiLinkModalVisible, setBidhiLinkModalVisible] = useState<boolean>(false);
  const [sankalpaLinkModalVisible, setSankalpaLinkModalVisible] = useState<boolean>(false);
  const [selectedPujaId, setSelectedPujaId] = useState<number | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [reorderMode, setReorderMode] = useState<{ pujaId: number; lineId: number } | null>(null);
  const [reorderedLinks, setReorderedLinks] = useState<Map<string, any[]>>(new Map());
  const [pujaLineReorderMode, setPujaLineReorderMode] = useState<number | null>(null);
  const [reorderedPujaLines, setReorderedPujaLines] = useState<Map<number, PujaLine[]>>(new Map());
  const [expandedPujas, setExpandedPujas] = useState<string[]>([]);
  const [pujaTags, setPujaTags] = useState<any[]>([]);
  const [loadingPujaTags, setLoadingPujaTags] = useState(false);
  const [creatingPujaTag, setCreatingPujaTag] = useState(false);
  const [pujaTagSearchValue, setPujaTagSearchValue] = useState('');
  const [activePujaTagFieldName, setActivePujaTagFieldName] = useState<number | null>(null);
  const [editPujaFileList, setEditPujaFileList] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('q') || '');
  const [tagFilterIds, setTagFilterIds] = useState<number[]>(
    searchParams.get('tags') ? searchParams.get('tags')!.split(',').map(Number).filter(n => !isNaN(n)) : []
  );
  const [duplicatingPujaId, setDuplicatingPujaId] = useState<number | null>(null);
  const navigate = useNavigate();

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [editPujaForm] = Form.useForm();
  const [editLineForm] = Form.useForm();
  const [addLineForm] = Form.useForm();

  useEffect(() => {
    dispatch(fetchPujas());
    if (!bidhis || bidhis.length === 0) {
      dispatch(fetchBidhis());
    }
    if (!sankalpas || sankalpas.length === 0) {
      dispatch(fetchSankalpas());
    }
  }, [dispatch]);

  const handleAddLines = async (values: { lines: { content: string; isVisible?: boolean; metadata?: { key: string; value: string }[] }[] }) => {
    try {
      const currentPuja = pujas.find(p => p.id === selectedPujaId);
      const maxOrder = currentPuja?.lines?.length ? Math.max(...currentPuja.lines.map(line => line.order || 0)) : 0;
      
      const linesWithOrder = values.lines.map((line, index) => {
        const metadata: Record<string, string> = {};
        if (line.metadata) {
          line.metadata.forEach(item => {
            if (item.key && item.value) {
              metadata[item.key] = item.value;
            }
          });
        }
        return {
          content: line.content,
          isVisible: line.isVisible ?? true,
          order: maxOrder + index + 1,
          pujaId: selectedPujaId!,
          ...(Object.keys(metadata).length ? { metadata } : {}),
        };
      });
      
      await dispatch(addPujaLines({ pujaId: selectedPujaId!, lines: linesWithOrder })).unwrap();
      message.success('Lines added successfully');
      setAddLineModalVisible(false);
      addLineForm.resetFields();
    } catch (error) {
      console.error('Error adding lines:', error);
      message.error('Failed to add lines');
    }
  };

  const handleAddPuja = async (values: (CreatePuja & { tagIds?: number[] }) | { formData: FormData; tagIds?: number[] }) => {
    try {
      let response: Puja;
      
      if ('formData' in values) {
        // Handle FormData (with profile picture)
        const { formData, tagIds } = values;
        const apiResponse = await pujaApi.create(formData);
        response = apiResponse.data;
        
        // Associate tags with puja
        if (tagIds && tagIds.length > 0 && response.id) {
          try {
            await Promise.all(
              tagIds.map(tagId => pujaTagAPI.associateTagWithPuja(tagId, response.id!))
            );
          } catch (tagError) {
            console.error('Error associating tags:', tagError);
            message.warning('Puja created but some tags may not have been associated');
          }
        }
      } else {
        // Handle regular JSON
        const { tagIds, ...pujaData } = values;
        response = await dispatch(createPuja(pujaData)).unwrap();
        
        // Associate tags with puja
        if (tagIds && tagIds.length > 0 && response.id) {
          try {
            await Promise.all(
              tagIds.map(tagId => pujaTagAPI.associateTagWithPuja(tagId, response.id!))
            );
          } catch (tagError) {
            console.error('Error associating tags:', tagError);
            message.warning('Puja created but some tags may not have been associated');
          }
        }
      }
      
      
      // Fetch complete puja data with tags and add to state
      if (response.id) {
        try {
          const completePujaResponse = await pujaApi.getById(response.id);
          dispatch(addPujaToState(completePujaResponse.data));
        } catch (fetchError) {
          console.error('Error fetching complete puja:', fetchError);
          // Fallback: add the response we have
          dispatch(addPujaToState(response));
        }
      } else {
        // If no ID, just add what we have
        dispatch(addPujaToState(response));
      }
      
      message.success('Puja created successfully');
      setAddPujaModalVisible(false);
    } catch (error) {
      console.error('Error creating puja:', error);
      message.error('Failed to create puja');
    }
  };

  const handleEditPuja = async (values: any) => {
    try {
      // Process metadata similar to GranthaPage
      const metadata: any = {
        bidha: values.bidha || '',
        upabidha: values.upabidha || '',
        prakar: values.prakar || '',
        upaprakar: values.upaprakar || '',
        aupaprakar: values.aupaprakar || '',
      };
      
      if (values.customMetadata) {
        values.customMetadata.forEach((item: { key: string; value: string }) => {
          if (item.key && item.value) {
            metadata[item.key] = item.value;
          }
        });
      }
      
      const hasProfilePicture = editPujaFileList.length > 0 && editPujaFileList[0].originFileObj;
      
      if (hasProfilePicture) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('metadata', JSON.stringify(metadata));
        formData.append('profilePicture', editPujaFileList[0].originFileObj);
        
        await pujaApi.update(selectedPujaId!, formData);
      } else {
      await dispatch(updatePuja({ 
        id: selectedPujaId!, 
        data: { 
          name: values.name,
          metadata: metadata
        } 
      })).unwrap();
      }
      
      // Update tags
      const currentPuja = pujas.find(p => p.id === selectedPujaId);
      const currentTagIds = currentPuja?.tags?.map(tag => tag.id) || [];
      const newTagIds = values.tagIds?.filter((id: any) => id !== undefined) || [];
      
      // Find tags to remove (in current but not in new)
      const tagsToRemove = currentTagIds.filter(id => !newTagIds.includes(id));
      
      // Find tags to add (in new but not in current)
      const tagsToAdd = newTagIds.filter((id: number) => !currentTagIds.includes(id));
      
      // Remove tags
      for (const tagId of tagsToRemove) {
        try {
          await pujaTagAPI.removeTagFromPuja(tagId, selectedPujaId!);
        } catch (error) {
          console.error('Error removing tag:', error);
        }
      }
      
      // Add tags
      for (const tagId of tagsToAdd) {
        try {
          await pujaTagAPI.associateTagWithPuja(tagId, selectedPujaId!);
        } catch (error) {
          console.error('Error adding tag:', error);
        }
      }
      
      message.success('Puja updated successfully');
      setEditPujaModalVisible(false);
      editPujaForm.resetFields();
    } catch (error) {
      console.error('Error updating puja:', error);
      message.error('Failed to update puja');
    }
  };

  const handleEditLine = async (values: { content: string; order?: number; isVisible?: boolean; metadata?: { key: string; value: string }[] }) => {
    try {
      const metadata: Record<string, string> = {};
      if (values.metadata) {
        values.metadata.forEach(item => {
          if (item.key && item.value) {
            metadata[item.key] = item.value;
          }
        });
      }

      await dispatch(
        updatePujaLine({
          lineId: selectedLineId!,
          data: {
            content: values.content,
            order: values.order,
            isVisible: values.isVisible,
            ...(Object.keys(metadata).length ? { metadata } : {}),
          },
        }),
      ).unwrap();
      message.success('Line updated successfully');
      setEditLineModalVisible(false);
      editLineForm.resetFields();
    } catch (error) {
      console.error('Error updating line:', error);
      message.error('Failed to update line');
    }
  };

  const handleDeleteLine = async (lineId: number) => {
    try {
      await dispatch(deletePujaLine(lineId)).unwrap();
      message.success('Line deleted successfully');
    } catch (error) {
      console.error('Error deleting line:', error);
      message.error('Failed to delete line');
    }
  };

  const handleDeletePuja = async (id: number) => {
    try {
      await dispatch(deletePuja(id)).unwrap();
      message.success('Puja deleted successfully');
    } catch (error) {
      console.error('Error deleting puja:', error);
      message.error('Failed to delete puja');
    }
  };

  const handleDuplicatePuja = async (puja: Puja) => {
    const sourceId = Number(puja?.id);
    if (!sourceId) return;

    try {
      setDuplicatingPujaId(sourceId);

      // Ensure we have the full Puja: lines + links
      const sourcePuja = (await pujaApi.getById(sourceId)).data;

      // 1) Copy name and add " copy"
      // 2) Copy profile picture reference (best-effort: backend may accept this string)
      // 3) Copy metadata
      // 4) Copy lines (then recreate links via network calls)
      const createPayload: any = {
        name: `${sourcePuja.name} copy`,
        ...(sourcePuja.metadata ? { metadata: { ...sourcePuja.metadata } } : {}),
        ...(sourcePuja.profilePicture ? { profilePicture: sourcePuja.profilePicture } : {}),
      };

      const created = (await pujaApi.create(createPayload)).data;
      if (!created?.id) throw new Error('Duplicate puja create did not return id');

      const sourceLines = [...(sourcePuja.lines || [])]
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      const linesToCreate = sourceLines.map((l: any) => ({
        content: l.content,
        order: l.order,
        isVisible: l.isVisible ?? true,
        ...(l.metadata && typeof l.metadata === 'object' && !Array.isArray(l.metadata) ? { metadata: l.metadata } : {}),
      }));

      let createdLines: any[] = [];
      if (linesToCreate.length > 0) {
        const addLinesResp = await pujaApi.addLines(created.id, linesToCreate);
        createdLines = (addLinesResp.data?.lines || addLinesResp.data || []) as any[];
      }

      const targetLines = [...createdLines].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      // Recreate links per line (network requests)
      for (let i = 0; i < sourceLines.length; i++) {
        const srcLine: any = sourceLines[i];
        const dstLine: any = targetLines[i];
        if (!dstLine?.id) continue;

        const bidhiLinks = [...(srcLine.bidhiLinks || [])].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        for (const link of bidhiLinks) {
          await pujaLineLinkApi.createBidhiLink({
            pujaLineId: dstLine.id,
            bidhiId: link.bidhiId,
            position: link.position || 0,
            order: link.order || 0,
            entireBidhi: !!link.entireBidhi,
            selectedBidhiLineIds: link.selectedBidhiLineIds || [],
          });
        }

        const sankalpaLinks = [...(srcLine.sankalpaLinks || [])];
        for (const link of sankalpaLinks) {
          await pujaLineLinkApi.createSankalpaLink({
            pujaLineId: dstLine.id,
            sankalpaId: link.sankalpaId,
            position: link.position || 0,
            selectedSankalpaLineIds: link.selectedSankalpaLineIds || [],
          });
        }
      }

      // Refresh state with the full duplicated puja
      const completeNew = (await pujaApi.getById(created.id)).data;
      dispatch(addPujaToState(completeNew));

      message.success('Puja duplicated successfully');
      navigate(`/pujaEdit/${created.id}`);
    } catch (error) {
      console.error('Error duplicating puja:', error);
      message.error('Failed to duplicate puja');
    } finally {
      setDuplicatingPujaId(null);
    }
  };

  const findCurrentLine = (): { puja: any; line: PujaLine } | null => {
    if (!selectedLineId) return null;
    for (const puja of pujas) {
      const line = puja.lines?.find((l: PujaLine) => l.id === selectedLineId);
      if (line) {
        return { puja, line };
      }
    }
    return null;
  };

  const handleBidhiLinkConfirm = async (
    selections: Array<{ bidhiId: number; order: number; entireBidhi: boolean; selectedLineIds: number[] }>,
    startingPosition?: number | null,
    titleVisible: boolean = true,
    linkAsShortcut?: boolean,
    displayName?: string,
  ) => {
    console.log('Puja-Bidhi link confirm payload:', {
      selections,
      startingPosition,
      selectedLineId,
    });
    try {
      const result = findCurrentLine();
      if (!result) {
        message.error('Unable to find selected line to link');
        return;
      }

      const { line } = result;
      const position = startingPosition ?? 0;
      
      // Find existing links at the same position to continue order
      const existingLinksAtPosition = (line.bidhiLinks || []).filter(
        (link) => link.position === position
      );
      const maxOrder = existingLinksAtPosition.length > 0
        ? Math.max(...existingLinksAtPosition.map(link => link.order ?? -1))
        : -1;
      const baseOrder = maxOrder + 1;
      
      // Make one API call for each selected bidhi
      const promises = selections.map(async (selection) => {
        const response = await pujaLineLinkApi.createBidhiLink({
          pujaLineId: line.id!,
          bidhiId: selection.bidhiId,
          position,
          order: baseOrder + selection.order, // Continue from existing max order
          entireBidhi: selection.entireBidhi,
          selectedBidhiLineIds: selection.entireBidhi ? [] : selection.selectedLineIds,
          titleVisible,
          shortcut: linkAsShortcut,
          displayName: linkAsShortcut ? displayName : undefined,
        });
        const createdLink = response.data as any;
        const bidhi = (bidhis as Bidhi[]).find(b => b.id === selection.bidhiId);
        dispatch(
          addPujaBidhiLink({
            pujaLineId: line.id!,
            link: {
              ...createdLink,
              titleVisible: createdLink.titleVisible ?? titleVisible,
              shorcut: createdLink.shorcut ?? linkAsShortcut,
              displayName: createdLink.displayName ?? displayName,
              bidhi: bidhi ? { id: bidhi.id, name: bidhi.name } : { id: selection.bidhiId, name: '' },
            },
          }),
        );
        return { bidhiId: selection.bidhiId, success: true };
      });

      await Promise.all(promises);
      message.success(`Linked puja line to ${selections.length} bidhi(s) successfully`);
      setBidhiLinkModalVisible(false);
    } catch (error) {
      console.error('Error linking puja line to bidhis:', error);
      message.error('Failed to link puja line to bidhis');
    }
  };

  const handleSankalpaLinkConfirm = async (
    sankalpaId: number,
    sankalpaLineIds?: number[],
    startingPosition?: number | null,
  ) => {
    console.log('Puja-Sankalpa link confirm payload:', {
      sankalpaId,
      sankalpaLineIds,
      startingPosition,
      selectedLineId,
    });
    try {
      const result = findCurrentLine();
      if (!result) {
        message.error('Unable to find selected line to link');
        return;
      }

      const { line } = result;
      // Call backend to create Sankalpa link for this Puja line
      const response = await pujaLineLinkApi.createSankalpaLink({
        pujaLineId: line.id!,
        sankalpaId,
        position: startingPosition ?? 0,
        selectedSankalpaLineIds: sankalpaLineIds && sankalpaLineIds.length ? sankalpaLineIds : [],
      });
      const createdLink = response.data as any;
      const sankalpa = (sankalpas as Sankalpa[]).find(s => s.id === sankalpaId);
      dispatch(
        addPujaSankalpaLink({
          pujaLineId: line.id!,
          link: {
            ...createdLink,
            sankalpa: sankalpa ? { id: sankalpa.id, name: sankalpa.name } : { id: sankalpaId, name: '' },
          },
        }),
      );

      message.success('Linked puja line to sankalpa successfully');
      setSankalpaLinkModalVisible(false);
    } catch (error) {
      console.error('Error linking puja line to sankalpa:', error);
      message.error('Failed to link puja line to sankalpa');
    }
  };

  const handleDeleteBidhiLink = async (linkId: number) => {
    try {
      await pujaLineLinkApi.deleteBidhiLink(linkId);
      dispatch(removePujaBidhiLink({ linkId }));
      message.success('Bidhi link detached successfully');
    } catch (error) {
      console.error('Error deleting bidhi link:', error);
      message.error('Failed to detach bidhi link');
    }
  };

  const handleBidhiLinkDragEnd = (event: any, pujaId: number, lineId: number, position: number) => {
    const { active, over } = event;
    if (active.id !== over?.id && over?.id) {
      const key = `${pujaId}-${lineId}-${position}`;
      const links = [...(reorderedLinks.get(key) || [])];
      const oldIndex = links.findIndex(link => `bidhi-link-${link.id}` === active.id);
      const newIndex = links.findIndex(link => `bidhi-link-${link.id}` === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newLinks = arrayMove(links, oldIndex, newIndex);
        setReorderedLinks(new Map(reorderedLinks.set(key, newLinks)));
      }
    }
  };

  const handlePujaLineDragEnd = (event: any, pujaId: number) => {
    const { active, over } = event;
    if (active.id !== over?.id && over?.id && pujaLineReorderMode === pujaId) {
      const lines = [...(reorderedPujaLines.get(pujaId) || [])];
      const oldIndex = lines.findIndex(line => `puja-line-${line.id}` === active.id);
      const newIndex = lines.findIndex(line => `puja-line-${line.id}` === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newLines = arrayMove(lines, oldIndex, newIndex);
        setReorderedPujaLines(new Map(reorderedPujaLines.set(pujaId, newLines)));
      }
    }
  };

  const handleTogglePujaLineReorderMode = (pujaId: number) => {
    if (pujaLineReorderMode === pujaId) {
      // Save changes
      handleSavePujaLineReorder(pujaId);
    } else {
      // Enter reorder mode
      const puja = pujas.find(p => p.id === pujaId);
      const sortedLines = puja?.lines ? [...puja.lines].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
      setReorderedPujaLines(new Map(reorderedPujaLines.set(pujaId, sortedLines)));
      setPujaLineReorderMode(pujaId);
    }
  };

  const handleSavePujaLineReorder = async (pujaId: number) => {
    try {
      const lines = reorderedPujaLines.get(pujaId);
      if (!lines) {
        setPujaLineReorderMode(null);
        return;
      }

      // Prepare bulk update payload
      const updates = lines.map((line, index) => ({
        pujaLineId: line.id!,
        changedOrder: index,
      }));

      await dispatch(bulkUpdatePujaLineOrder(updates)).unwrap();
      message.success('Line order updated successfully');
      setPujaLineReorderMode(null);
      
      // Clear reordered lines
      const newReorderedLines = new Map(reorderedPujaLines);
      newReorderedLines.delete(pujaId);
      setReorderedPujaLines(newReorderedLines);
    } catch (error) {
      console.error('Error updating line order:', error);
      message.error('Failed to update line order');
    }
  };

  const handleCancelPujaLineReorder = (pujaId: number) => {
    const newReorderedLines = new Map(reorderedPujaLines);
    newReorderedLines.delete(pujaId);
    setReorderedPujaLines(newReorderedLines);
    setPujaLineReorderMode(null);
  };

  const handleToggleReorderMode = (pujaId: number, lineId: number, position: number) => {
    const key = `${pujaId}-${lineId}-${position}`;
    const currentKey = reorderMode ? `${reorderMode.pujaId}-${reorderMode.lineId}-${position}` : null;
    
    if (reorderMode && currentKey === key) {
      // Save changes
      handleSaveReorder(pujaId, lineId, position);
    } else {
      // Enter reorder mode
      const puja = pujas.find(p => p.id === pujaId);
      const line = puja?.lines?.find(l => l.id === lineId);
      const linksAtPosition = (line?.bidhiLinks || []).filter(link => link.position === position);
      const sortedLinks = [...linksAtPosition].sort((a, b) => (a.order || 0) - (b.order || 0));
      setReorderedLinks(new Map(reorderedLinks.set(key, sortedLinks)));
      setReorderMode({ pujaId, lineId });
    }
  };

  const handleSaveReorder = async (pujaId: number, lineId: number, position: number) => {
    try {
      const key = `${pujaId}-${lineId}-${position}`;
      const links = reorderedLinks.get(key);
      if (!links) {
        setReorderMode(null);
        return;
      }

      const updates = links.map((link, index) => ({
        pujaBidhiLinkId: link.id,
        changedOrder: index
      }));

      await pujaLineLinkApi.bulkUpdateBidhiLinkOrder(updates);
      // Update state directly instead of fetching
      dispatch(updatePujaBidhiLinkOrder({ pujaLineId: lineId, position, updates }));
      message.success('Link order updated successfully');
      setReorderMode(null);
      // Clear reordered links
      const newReorderedLinks = new Map(reorderedLinks);
      newReorderedLinks.delete(key);
      setReorderedLinks(newReorderedLinks);
    } catch (error) {
      console.error('Error updating link order:', error);
      message.error('Failed to update link order');
    }
  };

  const handleCancelReorder = (pujaId: number, lineId: number, position: number) => {
    const key = `${pujaId}-${lineId}-${position}`;
    const newReorderedLinks = new Map(reorderedLinks);
    newReorderedLinks.delete(key);
    setReorderedLinks(newReorderedLinks);
    setReorderMode(null);
  };

  const handleDeleteSankalpaLink = async (linkId: number) => {
    try {
      await pujaLineLinkApi.deleteSankalpaLink(linkId);
      dispatch(removePujaSankalpaLink({ linkId }));
      message.success('Sankalpa link detached successfully');
    } catch (error) {
      console.error('Error deleting sankalpa link:', error);
      message.error('Failed to detach sankalpa link');
    }
  };

  const loadPujaTags = async () => {
    setLoadingPujaTags(true);
    try {
      const response = await pujaTagAPI.getAll();
      setPujaTags(response.data);
    } catch (error) {
      console.error('Error loading puja tags:', error);
    } finally {
      setLoadingPujaTags(false);
    }
  };

  const handlePujaTagSearch = (value: string) => {
    setPujaTagSearchValue(value);
  };

  const handleCreatePujaTag = async (fieldName?: number) => {
    const fieldToSet = fieldName !== undefined ? fieldName : activePujaTagFieldName;
    if (pujaTagSearchValue.trim()) {
      setCreatingPujaTag(true);
      try {
        const response = await pujaTagAPI.createTag({ name: pujaTagSearchValue.trim() });
        setPujaTags(prev => [...prev, response.data]);
        if (fieldToSet !== null) {
          // Set the newly created tag in the active field
          editPujaForm.setFieldValue(['tagIds', fieldToSet], response.data.id);
        }
        setPujaTagSearchValue('');
        setActivePujaTagFieldName(null);
        message.success('Tag created successfully');
      } catch (error) {
        console.error('Error creating tag:', error);
        message.error('Failed to create tag');
      } finally {
        setCreatingPujaTag(false);
      }
    }
  };

  const handleRemovePujaTag = async (tagId: number) => {
    if (!selectedPujaId) return;
    try {
      await pujaTagAPI.removeTagFromPuja(tagId, selectedPujaId);
      message.success('Tag removed successfully');
      // Refresh pujas to update tags
      dispatch(fetchPujas());
    } catch (error) {
      console.error('Error removing tag:', error);
      message.error('Failed to remove tag');
    }
  };

  useEffect(() => {
    if (editPujaModalVisible) {
      loadPujaTags();
    }
  }, [editPujaModalVisible]);

  const handleEditPujaClick = (puja: any) => {
    setSelectedPujaId(puja.id);
    const customMetadata = puja.metadata ? Object.entries(puja.metadata)
      .filter(([key]) => !['bidha', 'upabidha', 'prakar', 'upaprakar', 'aupaprakar'].includes(key))
      .map(([key, value]) => ({ key, value })) : [];
    
    editPujaForm.setFieldsValue({
      name: puja.name,
      bidha: puja.metadata?.bidha || '',
      upabidha: puja.metadata?.upabidha || '',
      prakar: puja.metadata?.prakar || '',
      upaprakar: puja.metadata?.upaprakar || '',
      aupaprakar: puja.metadata?.aupaprakar || '',
      customMetadata,
      tagIds: puja.tags?.map((tag: any) => tag.id) || [],
    });
    
    // Set existing profile picture if available
    if (puja.profilePicture) {
      setEditPujaFileList([{
        uid: '-1',
        name: puja.profilePicture,
        status: 'done',
        url: `${API_URL}/uploads/pujas/${puja.profilePicture}`,
      }]);
    } else {
      setEditPujaFileList([]);
    }
    
    setEditPujaModalVisible(true);
  };

  const handleEditLineClick = (puja: any, line: PujaLine) => {
    setSelectedPujaId(puja.id);
    setSelectedLineId(line.id!);
    const metadataArray =
      line.metadata
        ? Object.entries(line.metadata).map(([key, value]) => ({ key, value }))
        : [];

    editLineForm.setFieldsValue({
      content: line.content,
      order: line.order,
      isVisible: line.isVisible ?? true,
      metadata: metadataArray,
    });
    setEditLineModalVisible(true);
  };

  const tagOptions = useMemo(() => {
    const map = new Map<number, string>();
    (pujas || []).forEach((p: any) => {
      (p.tags || []).forEach((t: any) => {
        if (t?.id != null && typeof t?.name === 'string') map.set(Number(t.id), t.name);
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [pujas]);

  const filteredPujas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const tagSet = new Set(tagFilterIds);
    return (pujas || []).filter((p: any) => {
      const name = String(p?.name || '').toLowerCase();
      const desc = String(p?.metadata?.description || '').toLowerCase();
      const tagNames = (p?.tags || []).map((t: any) => String(t?.name || '').toLowerCase());

      const matchesText = !q || name.includes(q) || desc.includes(q) || tagNames.some((tn: string) => tn.includes(q));
      const matchesTag = tagSet.size === 0 || (p?.tags || []).some((t: any) => tagSet.has(Number(t?.id)));
      return matchesText && matchesTag;
    });
  }, [pujas, searchQuery, tagFilterIds]);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Puja Management</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddPujaModalVisible(true)}
        >
          Add Puja
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Input
          placeholder="Search by name, description, or tag..."
          value={searchQuery}
          onChange={(e: any) => {
            const val = e.target.value;
            setSearchQuery(val);
            const params = new URLSearchParams(searchParams);
            if (val) params.set('q', val);
            else params.delete('q');
            setSearchParams(params);
          }}
          allowClear
          style={{ width: 320, maxWidth: '100%' }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by tag"
          value={tagFilterIds}
          onChange={(vals: any) => {
            const ids = vals as number[];
            setTagFilterIds(ids);
            const params = new URLSearchParams(searchParams);
            if (ids.length > 0) params.set('tags', ids.join(','));
            else params.delete('tags');
            setSearchParams(params);
          }}
          options={tagOptions}
          style={{ minWidth: 260, maxWidth: '100%' }}
          maxTagCount="responsive"
        />
        <div style={{ color: '#666', fontSize: 12 }}>
          Showing <strong>{filteredPujas.length}</strong> / {pujas.length}
        </div>
      </div>
      <Card loading={loading}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 220px)',
            gap: 16,
            justifyContent: 'center',
          }}
        >
          {filteredPujas.map((puja) => (
            <Card
              key={puja.id?.toString() || ''}
              hoverable
              style={{
                width: 220,
                padding: 0,
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
              bodyStyle={{ padding: 0 }}
            >
                <div
                  style={{ position: 'relative', aspectRatio: '256/320', background: puja.profilePicture ? 'transparent' : '#f0f0f0', cursor: 'pointer' }}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (searchQuery) params.set('q', searchQuery);
                    if (tagFilterIds.length > 0) params.set('tags', tagFilterIds.join(','));
                    const queryString = params.toString();
                    navigate(`/pujaView/${puja.id}${queryString ? '?' + queryString : ''}`);
                  }}
                >
                  {/* Action buttons - top corners */}
                  <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, display: 'flex', gap: 4 }}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pujaEdit/${puja.id}`);
                      }}
                      style={{ background: 'rgba(255,255,255,0.95)', color: '#1890ff', border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                    />
                    <Popconfirm
                      title="Duplicate puja?"
                      description="Are you sure you want to create a copy of this puja?"
                      onConfirm={(e) => {
                        // prevent image click navigation
                        e?.stopPropagation();
                        handleDuplicatePuja(puja);
                      }}
                      onCancel={(e) => {
                        e?.stopPropagation();
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="primary"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          // only open confirm, do not navigate card
                          e.stopPropagation();
                        }}
                        loading={duplicatingPujaId === puja.id}
                        disabled={duplicatingPujaId !== null && duplicatingPujaId !== puja.id}
                        style={{ background: 'rgba(255,255,255,0.95)', color: '#1890ff', border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                      />
                    </Popconfirm>
                  </div>
                  
                  <Popconfirm
                    title="Delete puja?"
                    description="Are you sure you want to delete this puja?"
                    onConfirm={() => handleDeletePuja(puja.id!)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<CloseOutlined />}
                      size="small"
                      style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, background: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>

                  {puja.profilePicture ? (
                    <Image
                      src={`${API_URL}/uploads/pujas/${puja.profilePicture}`}
                      alt={puja.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      preview={false}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px' }}>
                      No Image
                    </div>
                  )}

                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
                    padding: '40px 12px 12px',
                    zIndex: 2
                  }}>
                    <div style={{ color: 'white', fontSize: '10px', fontWeight: 500, marginBottom: 4, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                      धर्मकर्म
                    </div>
                    {puja.metadata?.bidha && (
                      <div style={{ color: 'white', fontSize: '9px', fontWeight: 400, textShadow: '1px 1px 2px rgba(0,0,0,0.5)', lineHeight: '1.3' }}>
                        {puja.metadata.bidha}
                      </div>
                    )}
                  </div>

                  {/* Bottom overlay with id / bidhi and name */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.6), transparent)',
                    padding: '16px 12px 12px',
                    zIndex: 2
                  }}>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.85)',
                        fontSize: '10px',
                        marginBottom: 4,
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      ID: {puja.id} | {puja.metadata?.bidha}
                    </div>
                    <Typography.Title 
                      level={5} 
                      style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '13px', 
                        fontWeight: 600,
                        textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                        lineHeight: '1.4',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {puja.name}
                    </Typography.Title>
                    {puja.tags && puja.tags.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                        {puja.tags.slice(0, 2).map((tag: any) => (
                          <Tag key={tag.id} style={{ 
                            margin: 0, 
                            fontSize: '9px', 
                            padding: '2px 6px', 
                            background: 'rgba(255,255,255,0.25)', 
                            color: 'white', 
                            border: 'none',
                            borderRadius: '3px',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                          }}>
                            {tag.name}
                          </Tag>
                        ))}
                        {puja.tags.length > 2 && (
                          <Tag style={{ 
                            margin: 0, 
                            fontSize: '9px', 
                            padding: '2px 6px', 
                            background: 'rgba(255,255,255,0.25)', 
                            color: 'white', 
                            border: 'none',
                            borderRadius: '3px',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                          }}>
                            +{puja.tags.length - 2}
                          </Tag>
                        )}
                      </div>
                    )}
                  </div>
                </div>
            </Card>
          ))}
        </div>
      </Card>

      <AddPujaModal
        visible={addPujaModalVisible}
        onCancel={() => setAddPujaModalVisible(false)}
        onSubmit={handleAddPuja}
      />
    </div>
  );
};

export default PujaPage;
